import { useMemo } from "react";

import { useCart }
  from "../context/CartContext";

import { useAuth }
  from "../context/AuthContext";

import { useNavigate }
  from "react-router-dom";

import {
  ShoppingCart,
  Clock3,
  AlertTriangle,
  CalendarDays,
} from "lucide-react";

import CartHeader
  from "../components/cart/CartHeader";

import CartItem
  from "../components/cart/CartItem";

import CartSummary
  from "../components/cart/CartSummary";

import EmptyCart
  from "../components/cart/EmptyCart";

import {
  formatDisplayDate
} from "../utils/timeUtils";

function CartPage() {

  const {
    items,
    removeFromCart,
    clearCart,
    getTotalPrice,
    getDurationForDate,
  } = useCart();

  const {
    isAuthenticated
  } = useAuth();

  const navigate = useNavigate();

  /* ---------------------------------- */
  /* GROUP ITEMS BY DATE */
  /* ---------------------------------- */

  const itemsByDate = useMemo(() => {

    return items.reduce((acc, item) => {

      if (!acc[item.date]) {

        acc[item.date] = [];
      }

      acc[item.date].push(item);

      return acc;

    }, {});

  }, [items]);

  /* ---------------------------------- */
  /* DAILY LIMIT CHECK */
  /* ---------------------------------- */

  const dailyUsage =
    Object.keys(itemsByDate).map(
      (date) => ({

        date,

        duration:
          getDurationForDate(date),
      })
    );

  const nearLimitDates =
    dailyUsage.filter(
      (d) => d.duration >= 1.5
    );

  /* ---------------------------------- */
  /* AUTH */
  /* ---------------------------------- */

  if (!isAuthenticated) {

    return (

      <div style={styles.page}>

        <div style={styles.authCard}>

          <div style={styles.authIconCircle}>

            <ShoppingCart
              size={42}
              color="#00ff88"
            />

          </div>

          <h2 style={styles.authTitle}>
            Sign in to view your cart
          </h2>

          <p style={styles.authSubtitle}>
            Access your selected court bookings and continue checkout.
          </p>

          <button
            style={styles.authButton}
            onClick={() =>
              navigate("/signin")
            }
          >
            Sign In
          </button>

        </div>

      </div>
    );
  }

  /* ---------------------------------- */
  /* EMPTY */
  /* ---------------------------------- */

  const isEmpty =
    items.length === 0;

  return (

    <div style={styles.page}>

      <div style={styles.overlay}>

        <div style={styles.container}>

          {/* HEADER ALWAYS SHOWS */}

          <CartHeader
            count={items.length}
          />

          {/* EMPTY CART */}

          {isEmpty ? (

            <EmptyCart />

          ) : (

            <>
              {/* DAILY LIMIT WARNING */}

              {nearLimitDates.length > 0 && (

                <div style={styles.warningBanner}>

                  <div style={styles.warningLeft}>

                    <div style={styles.warningIcon}>

                      <AlertTriangle size={18} />

                    </div>

                    <div>

                      <div style={styles.warningTitle}>
                        Daily booking limit reminder
                      </div>

                      <div style={styles.warningText}>

                        Maximum allowed booking duration is

                        <span style={styles.highlight}>
                          {" "}2 hours per day
                        </span>

                      </div>

                    </div>

                  </div>

                </div>
              )}

              <div style={styles.layout}>

                {/* LEFT */}

                <div style={styles.itemsSection}>

                  {Object.entries(itemsByDate)

                    .sort(([a], [b]) =>
                      a.localeCompare(b)
                    )

                    .map(([date, dateItems]) => {

                      const duration =
                        getDurationForDate(date);

                      const limitReached =
                        duration >= 2;

                      return (

                        <div
                          key={date}
                          style={styles.dateCard}
                        >

                          {/* DATE HEADER */}

                          <div style={styles.dateHeader}>

                            <div>

                              <div style={styles.dateMeta}>

                                <CalendarDays
                                  size={14}
                                />

                                <span>
                                  Booking Date
                                </span>

                              </div>

                              <h3 style={styles.dateText}>
                                {formatDisplayDate(date)}
                              </h3>

                            </div>

                            <div
                              style={{
                                ...styles.durationBox,

                                border:
                                  limitReached

                                    ? "1px solid rgba(255,107,107,0.35)"

                                    : "1px solid rgba(0,255,136,0.2)",
                              }}
                            >

                              <div style={styles.durationTop}>

                                <Clock3 size={14} />

                                <span style={styles.durationLabel}>
                                  Daily Usage
                                </span>

                              </div>

                              <span
                                style={{
                                  ...styles.durationValue,

                                  color:
                                    limitReached
                                      ? "#ff6b6b"
                                      : "#00ff88",
                                }}
                              >
                                {duration}/2h
                              </span>

                            </div>

                          </div>

                          {/* LIMIT NOTICE */}

                          {limitReached && (

                            <div style={styles.limitReachedBox}>

                              <AlertTriangle
                                size={16}
                              />

                              <span>
                                Daily booking limit reached for this date
                              </span>

                            </div>
                          )}

                          {/* ITEMS */}

                          <div style={styles.itemList}>

                            {dateItems.map((item) => (

                              <CartItem
                                key={item.id}
                                item={item}
                                onRemove={
                                  removeFromCart
                                }
                              />

                            ))}

                          </div>

                        </div>

                      );
                    })}

                </div>

                {/* RIGHT */}

                <CartSummary
                  total={getTotalPrice()}
                  onClear={clearCart}
                  items={items}
                />

              </div>
            </>
          )}

        </div>

      </div>

    </div>
  );
}

const styles = {

  page: {

    minHeight: "100vh",

    background:
      "linear-gradient(111.1deg, rgb(0,40,70) -4.8%, rgb(255,229,115) 82.7%, rgb(255,203,15) 97.2%)",

    padding: "40px 20px",
  },

  overlay: {

    minHeight: "100vh",

    backdropFilter: "blur(3px)",
  },

  container: {

    maxWidth: "1200px",

    margin: "0 auto",
  },

  layout: {

    display: "grid",

    gridTemplateColumns:
      "2fr 1fr",

    gap: "25px",

    alignItems: "start",
  },

  itemsSection: {

    display: "flex",

    flexDirection: "column",

    gap: "24px",
  },

  warningBanner: {

    marginBottom: "22px",

    background:
      "rgba(255,193,7,0.12)",

    border:
      "1px solid rgba(255,193,7,0.22)",

    borderRadius: "18px",

    padding: "16px 18px",

    backdropFilter: "blur(12px)",
  },

  warningLeft: {

    display: "flex",

    alignItems: "center",

    gap: "14px",
  },

  warningIcon: {

    width: "36px",

    height: "36px",

    borderRadius: "50%",

    background:
      "rgba(255,193,7,0.16)",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    color: "#ffd54f",

    flexShrink: 0,
  },

  warningTitle: {

    color: "#fff",

    fontWeight: "700",

    marginBottom: "4px",
  },

  warningText: {

    color: "#d1d5db",

    fontSize: "14px",
  },

  highlight: {

    color: "#ffd54f",

    fontWeight: "700",
  },

  dateCard: {

    background:
      "rgba(0,40,70,0.72)",

    backdropFilter: "blur(12px)",

    borderRadius: "24px",

    padding: "24px",

    border:
      "1px solid rgba(0,255,150,0.15)",

    boxShadow:
      "0 10px 40px rgba(0,0,0,0.25)",
  },

  dateHeader: {

    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "center",

    marginBottom: "20px",

    gap: "16px",
  },

  dateMeta: {

    display: "flex",

    alignItems: "center",

    gap: "8px",

    color: "#9ca3af",

    fontSize: "13px",

    marginBottom: "8px",
  },

  dateText: {

    color: "#fff",

    margin: 0,

    fontSize: "22px",

    fontWeight: "700",
  },

  durationBox: {

    minWidth: "120px",

    padding: "12px 16px",

    borderRadius: "18px",

    background:
      "rgba(255,255,255,0.05)",

    textAlign: "right",
  },

  durationTop: {

    display: "flex",

    alignItems: "center",

    justifyContent: "flex-end",

    gap: "6px",

    marginBottom: "6px",

    color: "#9ca3af",
  },

  durationLabel: {

    fontSize: "12px",
  },

  durationValue: {

    fontSize: "26px",

    fontWeight: "800",
  },

  limitReachedBox: {

    display: "flex",

    alignItems: "center",

    gap: "10px",

    marginBottom: "18px",

    padding: "12px 14px",

    borderRadius: "14px",

    background:
      "rgba(239,68,68,0.12)",

    border:
      "1px solid rgba(239,68,68,0.25)",

    color: "#ff8b8b",

    fontSize: "13px",

    fontWeight: "600",
  },

  itemList: {

    display: "flex",

    flexDirection: "column",

    gap: "16px",
  },

  authCard: {

    maxWidth: "460px",

    margin: "120px auto",

    background:
      "rgba(0,40,70,0.85)",

    padding: "42px",

    borderRadius: "28px",

    textAlign: "center",

    border:
      "1px solid rgba(0,255,150,0.2)",

    backdropFilter: "blur(10px)",
  },

  authIconCircle: {

    width: "84px",

    height: "84px",

    borderRadius: "50%",

    background:
      "rgba(0,255,136,0.08)",

    border:
      "1px solid rgba(0,255,136,0.2)",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    margin: "0 auto 20px auto",
  },

  authTitle: {

    color: "#fff",

    marginBottom: "10px",

    fontSize: "28px",
  },

  authSubtitle: {

    color: "#cbd5e1",

    fontSize: "15px",

    lineHeight: "24px",
  },

  authButton: {

    marginTop: "26px",

    padding: "14px 28px",

    borderRadius: "999px",

    border: "none",

    background:
      "linear-gradient(90deg, #00ff88, #00d4ff)",

    color: "#041018",

    fontWeight: "700",

    cursor: "pointer",

    fontSize: "15px",

    transition:
      "all 0.2s ease",
  },
};

export default CartPage;